import { useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { ApiError } from '@/lib/api';
import {
  GenerateMealPlanRequestSchema,
  NutritionBudgetExceededError,
  NutritionNotConfiguredError,
  NutritionUpstreamError,
  useGenerateMealPlan,
  type GenerateMealPlanRequest,
  type MealPlanPreferences,
} from '@/lib/nutrition';
import { useJobs } from '@/lib/jobs';

/**
 * Formulario para generar un plan semanal de comidas.
 *
 * Mismo patrón que `coach/NewPlanForm`:
 * - Validación client-side con el mismo schema Zod que valida el backend.
 * - Preview por defecto = true.
 * - Spinner mientras isPending; mapeo de errores a mensajes humanos
 *   reutilizando los tipos `NutritionXxxError` (alias de los del coach).
 *
 * Decisión sobre alergias: el contrato pide `allergies: string[]`. Se
 * recoge como CSV en un único input por usabilidad ("nueces, gluten, fresa")
 * y se transforma a array al construir el request — patrón estándar para
 * listas cortas, evita exponer un componente tag-input que tampoco existe en
 * la librería de UI propia.
 */
export function GenerateMealPlanForm({
  defaultWeekStartISO,
  onSuccess,
}: {
  defaultWeekStartISO?: string;
  onSuccess?: (weekStartISO: string) => void;
}): ReactElement {
  const mutation = useGenerateMealPlan();
  const { startJob } = useJobs();

  const initialWeekDate = useMemo(
    () =>
      defaultWeekStartISO
        ? isoToDateInput(defaultWeekStartISO)
        : nextMondayISODate(),
    [defaultWeekStartISO],
  );

  const [weekStart, setWeekStart] = useState<string>(initialWeekDate);
  const [vegetarian, setVegetarian] = useState(false);
  const [vegan, setVegan] = useState(false);
  const [lactoseFree, setLactoseFree] = useState(false);
  const [glutenFree, setGlutenFree] = useState(false);
  const [allergiesCsv, setAllergiesCsv] = useState('');
  const [targetKcal, setTargetKcal] = useState<string>('');
  const [constraints, setConstraints] = useState('');
  // M2.26 — default `preview: false`: el plan se persiste directamente.
  // El usuario quiere que si navega durante la generación (30-90s) el plan
  // quede guardado al volver. Antes era `true` y obligaba a no moverse.
  const [preview, setPreview] = useState(false);
  // M2.26 — accordion "personalización avanzada" colapsado por defecto.
  // Default: el form sólo pide la semana, el backend deriva kcal del peso
  // + entrenos planificados de esa semana.
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function buildRequest(): GenerateMealPlanRequest {
    // M2.30 — El backend nutrition (routes/nutrition.ts) acepta SOLO el
    // formato YYYY-MM-DD (regex /^\d{4}-\d{2}-\d{2}$/), no el ISO 8601
    // completo. Antes enviábamos `.toISOString()` y daba 400 'Petición
    // inválida'. El input `<input type="date">` ya entrega YYYY-MM-DD,
    // así que basta con pasarlo tal cual.
    const weekStartISO = weekStart;

    const allergies = allergiesCsv
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const preferences: MealPlanPreferences = {};
    if (vegetarian) preferences.vegetarian = true;
    if (vegan) preferences.vegan = true;
    if (lactoseFree) preferences.lactoseFree = true;
    if (glutenFree) preferences.glutenFree = true;
    if (allergies.length > 0) preferences.allergies = allergies;

    const req: GenerateMealPlanRequest = {
      weekStartISO,
      preview,
    };
    if (Object.keys(preferences).length > 0) req.preferences = preferences;

    const targetKcalNum = Number(targetKcal);
    if (Number.isFinite(targetKcalNum) && targetKcalNum > 0) {
      req.targetKcal = Math.round(targetKcalNum);
    }
    const trimmedConstraints = constraints.trim();
    if (trimmedConstraints) req.constraints = trimmedConstraints;
    return req;
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (mutation.isPending) return;
    const candidate = buildRequest();
    const parsed = GenerateMealPlanRequestSchema.safeParse(candidate);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? '');
        if (key && !next[key]) next[key] = issue.message;
      }
      setFieldErrors(next);
      return;
    }
    setFieldErrors({});
    // M2.27 — mutation + job global persistente para que el banner
    // flotante en AppLayout muestre estado durante navegaciones.
    const promise = mutation.mutateAsync(parsed.data);
    startJob({
      type: 'meal-plan',
      label: `Nutricionista · semana del ${shortDateLabel(parsed.data.weekStartISO)}`,
      promise,
      resultPath: '/nutricion',
    });
    promise.then(
      (result) => {
        if (onSuccess) onSuccess(result.plan.weekStartISO);
      },
      () => {
        /* banner pinta el error; el form también muestra mutation.error */
      },
    );
  }

  function shortDateLabel(iso: string): string {
    try {
      return new Intl.DateTimeFormat('es-ES', {
        day: 'numeric',
        month: 'short',
      }).format(new Date(iso));
    } catch {
      return iso.slice(0, 10);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="space-y-6"
      aria-labelledby="nutrition-form-title"
    >
      <header>
        <h2 id="nutrition-form-title" className="text-xl font-semibold tracking-tight text-slate-900">
          Generar plan de comidas
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          El nutricionista IA calculará tus kcal/CHO/proteína a partir de
          tu peso y los entrenos planificados de la semana. Tarda 30-90
          segundos — puedes navegar mientras se genera, el plan queda
          guardado al terminar.
        </p>
      </header>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Semana
        </h3>
        <div className="mt-3">
          <label htmlFor="meal-weekStart" className="block text-sm font-medium text-slate-700">
            Lunes de la semana objetivo
          </label>
          <input
            id="meal-weekStart"
            type="date"
            value={weekStart}
            onChange={(e) => setWeekStart(e.target.value)}
            aria-invalid={fieldErrors.weekStartISO !== undefined}
            aria-describedby={fieldErrors.weekStartISO ? 'meal-weekStart-error' : undefined}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600 sm:w-auto"
          />
          {fieldErrors.weekStartISO && (
            <p id="meal-weekStart-error" role="alert" className="mt-1 text-xs text-rose-700">
              {fieldErrors.weekStartISO}
            </p>
          )}
          <p className="mt-1.5 text-xs text-slate-500">
            El plan cubre los 7 días (Lun→Dom) a partir de la fecha elegida. Por
            defecto va al lunes siguiente. <strong>No es el día de tu carrera</strong>{' '}
            — para planificar la semana de una prueba, mete el lunes anterior a esa fecha.
          </p>
        </div>
      </section>

      {/* M2.26 — Personalización avanzada en accordion colapsado. Default
          es: nutricionista usa peso + planned workouts de la semana para
          derivar targetKcal y deja preferencias vacías. */}
      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          aria-expanded={showAdvanced}
          aria-controls="meal-advanced"
          data-testid="meal-advanced-toggle"
          className="flex w-full items-center justify-between text-left"
        >
          <span>
            <span className="block text-sm font-semibold uppercase tracking-wide text-slate-500">
              Personalización avanzada
            </span>
            <span className="mt-0.5 block text-xs text-slate-600">
              Preferencias dietéticas, kcal objetivo, restricciones libres.
              Si no las tocas, el nutricionista decide.
            </span>
          </span>
          <span aria-hidden="true" className="text-lg text-slate-400">
            {showAdvanced ? '−' : '+'}
          </span>
        </button>
        {showAdvanced && (
          <div id="meal-advanced" className="mt-4 space-y-4">
            <fieldset>
              <legend className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Restricciones dietéticas
              </legend>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <CheckboxField id="pref-vegetarian" checked={vegetarian} onChange={setVegetarian} label="Vegetariano" />
                <CheckboxField id="pref-vegan" checked={vegan} onChange={setVegan} label="Vegano" />
                <CheckboxField id="pref-lactose" checked={lactoseFree} onChange={setLactoseFree} label="Sin lactosa" />
                <CheckboxField id="pref-gluten" checked={glutenFree} onChange={setGlutenFree} label="Sin gluten" />
              </div>
            </fieldset>

            <div>
              <label htmlFor="pref-allergies" className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Alergias (separadas por comas)
              </label>
              <input
                id="pref-allergies"
                type="text"
                value={allergiesCsv}
                onChange={(e) => setAllergiesCsv(e.target.value)}
                placeholder="Ej: nueces, fresa, marisco"
                className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
              />
            </div>

            <div>
              <label htmlFor="pref-targetkcal" className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Kcal totales por día (opcional)
              </label>
              <input
                id="pref-targetkcal"
                type="number"
                min={500}
                max={8000}
                inputMode="numeric"
                value={targetKcal}
                onChange={(e) => setTargetKcal(e.target.value)}
                aria-invalid={fieldErrors.targetKcal !== undefined}
                placeholder="Auto"
                className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600 sm:w-40"
              />
              <p className="mt-1 text-xs text-slate-500">
                Vacío → BMR(peso/edad/sexo) × factor actividad de los entrenos planificados.
              </p>
            </div>

            <div>
              <label htmlFor="meal-constraints" className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Restricciones libres
              </label>
              <textarea
                id="meal-constraints"
                rows={3}
                maxLength={1000}
                value={constraints}
                onChange={(e) => setConstraints(e.target.value)}
                placeholder="Ej: cocino batch los domingos, no me gustan las legumbres con leche."
                aria-invalid={fieldErrors.constraints !== undefined}
                className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
              />
              <p className="mt-1 text-xs text-slate-500">{constraints.length} / 1000 caracteres</p>
            </div>

            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                checked={preview}
                onChange={(e) => setPreview(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-600"
                data-testid="meal-preview-toggle"
              />
              <span>
                <span className="font-medium text-slate-900">Solo previsualizar</span>
                <span className="block text-xs text-slate-600">
                  No persiste el plan en BD; si navegas mientras se genera, lo pierdes.
                </span>
              </span>
            </label>
          </div>
        )}
      </section>

      {mutation.isPending && (
        <div
          role="status"
          aria-live="polite"
          aria-busy="true"
          className="flex items-center gap-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <Spinner />
          <span>
            El nutricionista está pensando…{' '}
            <span className="text-xs text-amber-800">
              (esto puede tardar hasta 30 segundos)
            </span>
          </span>
        </div>
      )}

      {mutation.isError && <ErrorBanner error={mutation.error} />}

      {mutation.isSuccess && !mutation.isPending && (
        <div
          role="status"
          aria-live="polite"
          className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
        >
          Plan generado.{' '}
          {mutation.data.persisted === false
            ? 'Guarda los cambios desde la pestaña "Plan semanal" si te convencen.'
            : 'Disponible en la pestaña "Plan semanal".'}
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        <button
          type="submit"
          disabled={mutation.isPending}
          aria-busy={mutation.isPending}
          className="rounded-md bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? (
            <span className="inline-flex items-center gap-2">
              <Spinner /> Generando…
            </span>
          ) : (
            'Generar plan'
          )}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------

function CheckboxField({
  id,
  checked,
  onChange,
  label,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}): ReactElement {
  return (
    <label
      htmlFor={id}
      className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition ${
        checked
          ? 'border-teal-600 bg-teal-50 text-teal-900'
          : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
      }`}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-600"
      />
      <span>{label}</span>
    </label>
  );
}

function Spinner(): ReactElement {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

// ---------------------------------------------------------------------------
// Error banner
// ---------------------------------------------------------------------------

function ErrorBanner({ error }: { error: Error }): ReactElement {
  return (
    <div role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
      {mapNutritionError(error)}
    </div>
  );
}

function mapNutritionError(err: Error): string {
  if (err instanceof NutritionBudgetExceededError) {
    return (
      err.hint ||
      'Has alcanzado tu presupuesto mensual de Anthropic. Súbelo en /ajustes/anthropic o espera al mes siguiente.'
    );
  }
  if (err instanceof NutritionNotConfiguredError) {
    return `Falta configurar tu ANTHROPIC_API_KEY. Configúrala en /ajustes/anthropic.${
      err.hint ? ` ${err.hint}` : ''
    }`;
  }
  if (err instanceof NutritionUpstreamError) {
    return 'Error de Anthropic, vuelve a intentar en un momento.';
  }
  if (err instanceof ApiError && err.status === 422) {
    return `El nutricionista generó un plan inválido. ${err.message ?? ''}`.trim();
  }
  if (err instanceof ApiError && err.status === 400) {
    return `Petición inválida. ${err.message ?? 'Revisa los campos.'}`;
  }
  return `No se pudo generar el plan. ${err.message ?? ''}`.trim();
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function nextMondayISODate(): string {
  const now = new Date();
  const day = now.getDay();
  const daysUntilNextMon = ((1 - day + 7) % 7) || 7;
  const next = new Date(now);
  next.setDate(now.getDate() + daysUntilNextMon);
  const yyyy = next.getFullYear();
  const mm = String(next.getMonth() + 1).padStart(2, '0');
  const dd = String(next.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function isoToDateInput(iso: string): string {
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return nextMondayISODate();
  }
}
